/**
 * MyKitPage — vista personale dello Store User sui propri attrezzi.
 *
 * Mostra:
 *  • Header con counter "N attrezzi affidati"
 *  • Mini-KPI: totale, valore (info OPS-only se l'utente la vede), foto
 *    presenti vs mancanti, manutenzioni cumulative
 *  • Lista card con codice, categoria, modello, negozio, link al dettaglio
 *
 * Accessibile solo via la voce sidebar "Il mio kit" filtrata su
 * role==='store_user' (vedi Layout.js). Backend riusa
 * `GET /api/inventory/items?assigned_user_id=<my>` con scoping standard.
 */
import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Package, Wrench, Camera, Store } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { inventoryApi, storesApi } from '../lib/api';

export default function MyKitPage() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [storeMap, setStoreMap] = useState({});

  useEffect(() => {
    if (!user?.user_id) return;
    let alive = true;
    Promise.all([
      inventoryApi.listItems({ assigned_user_id: user.user_id }).catch(() => ({ data: [] })),
      storesApi.list().catch(() => ({ data: [] })),
    ]).then(([li, st]) => {
      if (!alive) return;
      setItems(li.data || []);
      const stores = Array.isArray(st.data) ? st.data : (st.data?.stores || []);
      const map = {};
      stores.forEach(s => { map[s.store_id] = s.display_name || s.name; });
      setStoreMap(map);
    });
    return () => { alive = false; };
  }, [user?.user_id]);

  const stats = useMemo(() => {
    if (!items) return null;
    const total = items.length;
    const photos = items.filter(i => (i.photo_count || 0) > 0).length;
    const maint = items.reduce((s, i) => s + (i.maintenance_count || 0), 0);
    const value = items.reduce((s, i) => s + (i.purchase_price || 0), 0);
    return { total, photos, missing: total - photos, maint, value };
  }, [items]);

  if (!items) {
    return <div className="p-4 md:p-8 text-sm text-slate-500">Caricamento…</div>;
  }

  return (
    <div className="p-4 md:p-8 max-w-[1920px] space-y-8" data-testid="my-kit-page">
      <header className="flex items-start justify-between gap-4 flex-wrap pb-6 border-b border-slate-200">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-md border border-slate-300 bg-slate-50 flex items-center justify-center shrink-0">
            <Package className="w-5 h-5 text-slate-700" />
          </div>
          <div>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Il mio kit</h2>
            <p className="text-sm text-slate-500 mt-1">
              Gli attrezzi assegnati a te — manutenzione, foto, dettagli
            </p>
          </div>
        </div>
      </header>

      {/* Mini KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-100 rounded-md overflow-hidden border border-slate-200">
        <KpiCell label="Attrezzi" value={stats.total} testId="kit-total" />
        <KpiCell
          label="Con foto"
          value={`${stats.photos}/${stats.total}`}
          sub={stats.missing > 0 ? `${stats.missing} senza foto` : 'tutti documentati'}
          tone={stats.missing > 0 ? 'warn' : 'ok'}
          testId="kit-photos"
        />
        <KpiCell label="Manutenzioni" value={stats.maint} testId="kit-maint" />
        <KpiCell
          label="Valore"
          value={`€${Number(stats.value || 0).toLocaleString('it-IT', { maximumFractionDigits: 0 })}`}
          testId="kit-value"
        />
      </div>

      {/* Lista */}
      {stats.total === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="kit-items">
          {items.map(it => (
            <KitItemCard key={it.item_id} item={it} storeName={storeMap[it.store_id]} />
          ))}
        </div>
      )}
    </div>
  );
}

function KpiCell({ label, value, sub, tone, testId }) {
  const toneCls = tone === 'warn' ? 'text-amber-700' : 'text-slate-900';
  return (
    <div className="bg-white p-4" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${toneCls}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function KitItemCard({ item, storeName }) {
  const hasPhotos = (item.photo_count || 0) > 0;
  return (
    <Link
      to={`/inventory/${item.item_id}`}
      state={{ backTo: '/my-kit', backLabel: 'Il mio kit' }}
      className="block bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-400 hover:shadow-sm transition"
      data-testid={`kit-item-${item.code}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="font-mono text-xs font-semibold bg-slate-100 px-2 py-0.5 rounded">
          {item.code}
        </span>
        {hasPhotos ? (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-900 text-white text-[10px] font-medium"
            title="Foto disponibili"
          >
            <Camera className="w-3 h-3" />{item.photo_count}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-medium">
            <Camera className="w-3 h-3" />no foto
          </span>
        )}
      </div>
      <p className="text-sm font-semibold text-slate-900 truncate">
        {item.category_name || '—'}
        {item.model && <span className="text-slate-500 font-normal"> · {item.model}</span>}
      </p>
      <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
        {storeName && (
          <span className="inline-flex items-center gap-1">
            <Store className="w-3 h-3" />{storeName}
          </span>
        )}
        {item.maintenance_count > 0 && (
          <span className="inline-flex items-center gap-1">
            <Wrench className="w-3 h-3" />{item.maintenance_count} manut.
          </span>
        )}
      </div>
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="border border-slate-200 rounded-lg p-10 text-center bg-slate-50/50" data-testid="kit-empty">
      <Package className="w-10 h-10 mx-auto text-slate-300" />
      <p className="mt-3 text-sm text-slate-700 font-medium">Non hai attrezzi assegnati</p>
      <p className="mt-1 text-xs text-slate-500 max-w-md mx-auto">
        Chiedi al tuo Area Manager di affidarti la strumentazione che usi nel negozio.
        Una volta assegnata, comparirà qui con tutti i dettagli e lo storico.
      </p>
    </div>
  );
}
